import {
  GammaMapLayerType,
  ILayerControl,
  IMapLayerGroup,
  IMapLayerLegend,
} from "@/types/index";
import { snakeCase } from "lodash-es";
import { LayerControl } from "@/types/LayerControl";
import { useMap } from "@/services";

export class MapLayerGroup implements IMapLayerGroup, ILayerControl {
  name = "";
  type = GammaMapLayerType.Group;
  layers = [] as (string | IMapLayerGroup)[];
  parents = [] as string[];
  source = "";
  legend = {} as IMapLayerLegend;
  private _isVisible = true;
  private _opacity = 1;

  constructor(data: ILayerControl & (IMapLayerGroup | MapLayerGroup)) {
    Object.assign(this, { ...data });
    this.setSubLayerGroups();
    if (this.type === GammaMapLayerType.Composite) {
      useMap().addLayerControl(
        new LayerControl({
          id: this.id,
          isVisible: this.isVisible,
          opacity: this.opacity,
          path: this.path,
          editable: data.editable,
          layerIds: this.layers.filter(
            (l) => typeof l === "string"
          ) as string[],
        })
      );
    } else if (data.type === GammaMapLayerType.Style) {
      useMap().addLayerControl(
        new LayerControl({
          id: this.id,
          isVisible: this.isVisible,
          opacity: 1,
          path: this.path,
          isStyleLayer: true,
          layerIds: this.allLayers,
        })
      );
    } else if (data.type !== GammaMapLayerType.SubGroup) {
      this.layers
        .filter((l) => typeof l === "string")
        .forEach((l) =>
          useMap().addLayerControl(
            new LayerControl({
              id: l as string,
              isVisible: this.isVisible,
              opacity: 1,
              path: this.path,
              editable: data.editable,
            })
          )
        );
    }
  }

  setSubLayerGroups() {
    this.layers = this.layers.map((l) => {
      if (typeof l === "string") return l;
      const subGroup = this.toMapLayerGroup(l);
      useMap().addLayerGroup(subGroup);
      return subGroup;
    });
  }

  addSubLayer(data: IMapLayerGroup) {
    const subGroup = this.toMapLayerGroup(data);
    this.layers.push(subGroup);
    useMap().addLayerGroup(subGroup);
  }

  removeSubLayer(name: string) {
    const layer = this.layers.find(
      (x) => x instanceof MapLayerGroup && (x as MapLayerGroup).name === name
    );

    if (!layer) return;

    useMap().removeLayerGroup((layer as MapLayerGroup).id);
    useMap().removeLayerControl((layer as MapLayerGroup).id);
    this.layers = this.layers.filter(
      (x) => !(x instanceof MapLayerGroup && (x as MapLayerGroup).name === name)
    );
  }

  toMapLayerGroup(data: IMapLayerGroup): MapLayerGroup {
    return new MapLayerGroup({
      ...data,
      type:
        this.type === GammaMapLayerType.Style
          ? GammaMapLayerType.SubGroup
          : data.type,
      parents: [...this.parents, snakeCase(this.name)] as string[],
    });
  }

  get id() {
    return snakeCase(this.name);
  }

  get allLayers(): string[] {
    return this.layers
      .map((l) =>
        typeof l === "string"
          ? [l]
          : (this.layerGroups.find((lg) => lg.id === l.id) || { allLayers: [] })
              .allLayers
      )
      .flat();
  }

  get layerControls(): LayerControl[] {
    return (
      this.hasLayerControl
        ? [useMap().layerControls.find((lc) => lc.id === this.id)]
        : this.allLayers.map((l) =>
            useMap().layerControls.find((lc) => lc.id === l)
          )
    ).filter((l) => l) as LayerControl[];
  }

  get hasLayerControl(): boolean {
    return [GammaMapLayerType.Composite, GammaMapLayerType.Style].includes(
      this.type
    );
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(value: number) {
    if (value !== this.opacity) {
      this._opacity = value;
      this.layerControls.forEach((lc: LayerControl) => lc.updateOpacity());
    }
  }

  get isVisible() {
    return this._isVisible;
  }

  set isVisible(value: boolean) {
    if (value !== this.isVisible) {
      this._isVisible = value;
      this.layerGroups.forEach((lg) => {
        lg.isVisible = this._isVisible;
      });
      this.layerControls.forEach((lc: LayerControl) => lc.updateVisibility());
    }
  }

  get layerGroups() {
    return this.layers.filter((l) => typeof l !== "string") as MapLayerGroup[];
  }

  get path(): string {
    return [...this.parents, this.id].join(".");
  }
}
